import { useState } from "react";
import SectionLabel from "@/components/SectionLabel";
import { ChevronDown, Phone, CalendarClock } from "lucide-react";

const sacraments = [
  {
    n: "i",
    title: "Baptism",
    subtitle: "The First Embrace of God",
    schedule: "Every Sunday · 9:30 AM",
    description: [
      "Baptism is the first sacred gift parents can offer their child. Through Baptism, a child is welcomed into the family of God, cleansed from sin, marked with the grace of Christ, and begins the beautiful journey of faith with the Church.",
      "To bring a child for Baptism is to say: \u201cLord, this child belongs not only to us, but first of all to You.\u201d It is an act of love, faith, and hope \u2014 placing the child\u2019s life under God\u2019s blessing, guidance, and protection.",
      "In Baptism, a child receives not only a name, but a Christian identity: a child of God, a member of the Church, and a bearer of Christ\u2019s light.",
      "Please note that the Baptism will be scheduled one month after all the required documents have been properly completed and submitted. This allows the parents to prepare not only physically, but also spiritually, and gives the Parish Office enough time to verify that all requirements are complete and in order.",
      "Parents and godparents are also required to attend the baptismal preparation sessions conducted by their respective GKK volunteer catechists. These sessions will be held at the GKK Chapel as part of the spiritual preparation for this important sacrament.",
    ],
    requirements: [
      "GKK Certification signed by the Unit President and noted by the GKK President.",
      "Child\u2019s Birth Certificate (photocopy).",
      "Parents\u2019 Marriage Contract \u2014 if both parents are married Catholic (photocopy).",
      "Mother\u2019s Baptismal Certificate \u2014 if not married (photocopy).",
      "Death Certificate \u2014 if one of the parents has died (photocopy).",
      "Parents who are not married and have a child to be baptized must schedule a meeting with the Parish Priest.",
    ],
    extra: "Interview: Every Thursday, 2:00 PM \u00b7 Parish Office",
  },
  {
    n: "ii",
    title: "Confirmation",
    subtitle: "Strengthened by the Holy Spirit",
    schedule: "By GKK Catechist arrangement",
    description: [
      "Confirmation is the sacrament that completes and strengthens the grace first received in Baptism. Through the gift of the Holy Spirit, a baptized person is sealed with God\u2019s presence and empowered to live as a faithful witness of Christ.",
      "In Confirmation, faith becomes more than something received from parents and the Church; it becomes a personal commitment to follow Jesus, defend the faith, serve the community, and bring Christ\u2019s light into the world.",
      "Confirmation reminds us: we are not only children of God \u2014 we are sent as witnesses of Christ, strengthened by the Holy Spirit.",
      "The GKK catechists oversee the catechetical training of candidates preparing for the Sacrament of Confirmation. For further details regarding schedules, preparation, and other parish guidelines, please consult your Unit President, GKK President, and GKK catechists.",
    ],
    requirements: [
      "Birth Certificate (photocopy).",
      "Baptismal Certificate (photocopy).",
      "First Communion Certificate (photocopy).",
      "Fully accomplished Confirmation Form.",
    ],
  },
  {
    n: "iii",
    title: "Matrimony",
    subtitle: "Love Blessed and Sealed by God",
    schedule: "Every Saturday · 7:30 AM (regular wedding)",
    description: [
      "Matrimony is the sacrament in which a man and a woman freely give themselves to each other in faithful, lifelong, and fruitful love. In this sacred covenant, their love is not only witnessed by family and friends; it is blessed, strengthened, and sealed by God.",
      "Through the Sacrament of Matrimony, the couple becomes a living sign of Christ\u2019s faithful love for the Church. Their marriage becomes a vocation \u2014 a call to love, forgive, serve, and build a home where God is present.",
      "In Matrimony, two hearts do not simply promise to love each other; they invite God to dwell at the center of their life together.",
      "Couples are asked to schedule their wedding with the Parish Office at least two months before the intended date. This preparation period is necessary to ensure all documents are properly prepared, submitted, and verified \u2014 especially because some applicants may need to secure Publication of Marriage Banns from other parishes, dioceses, or abroad.",
    ],
    requirements: [
      "Application Form for Marriage.",
      "Baptismal Certificate \u2014 original copy (newly issued for marriage purposes).",
      "Confirmation Certificate \u2014 original copy (newly issued).",
      "Permit to Marry / Parish Permit (if coming from another parish).",
      "For Non-Catholics: Certificate of Liberty from the Pastor, Barangay Clearance / CENOMAR (photocopy).",
      "Cana 101 Seminar by the GKK FLM worker (Venue: GKK Chapel).",
      "Marriage License \u2014 original (from City Hall).",
      "For Validation: Marriage Contract (Civil or other Sect/Religion) \u2014 original & 1 photocopy.",
      "Final Interview with the Priest \u2014 every Thursday at 2:00 PM.",
      "Dispensation Paper for Non-Catholic.",
      "Letter of Approval from Embassy / Legal Capacity to Marry (for foreigners).",
      "Certificate of Bachelorhood from the CO (for PNP / Military).",
      "Submit a copy of wedding sponsors during the Final Interview.",
      "Payments arranged on or before the Final Interview (couple & sponsors\u2019 fees).",
      "Affidavit of Cohabitation (Article 34) / Birth Certificate of Bride & Groom.",
      "4 pcs. Documentary Stamp (BIR Office).",
      "CENOMAR from NSO (for Article 34) / Death Certificate (for widower / widow).",
    ],
    extra:
      "Submit the Wedding Application Form, Baptismal & Confirmation Certificates before attending the Cana 101 Seminar.",
  },
  {
    n: "iv",
    title: "Anointing of the Sick",
    subtitle: "God\u2019s Healing Touch in Time of Illness",
    schedule: "Urgent & regular calls accepted anytime",
    description: [
      "The Anointing of the Sick is the sacrament of Christ\u2019s comfort, strength, and healing for those who are seriously ill, weakened by old age, preparing for surgery, or facing physical, emotional, or spiritual suffering.",
      "Through the prayer of the priest and the anointing with holy oil, the sick person receives God\u2019s grace: peace in fear, courage in pain, forgiveness of sins, and the loving presence of Christ who never abandons His people in their weakness.",
      "This sacrament is not only for those near death. It is a sacrament of hope for the sick, the elderly, and all who need God\u2019s healing mercy.",
      "In the Anointing of the Sick, Christ comes close to the suffering person and says: \u201cYou are not alone. I am with you.\u201d",
    ],
    requirements: [
      "Contact the Parish Office at 0915 581 1419 for urgent and regular scheduling.",
      "Complete the Sick Call Form so the request can be properly recorded and arranged.",
    ],
  },
  {
    n: "v",
    title: "Confession & Holy Hour",
    subtitle: "Reconciled with the Mercy of Christ",
    schedule: "Every Friday · 7:00 AM",
    description: [
      "Confession may also be requested before scheduled parish celebrations or by appointment through the parish office.",
      "Holy Hour follows Confession every Friday \u2014 a quiet moment of Eucharistic prayer and adoration.",
    ],
    requirements: [
      "Sincere preparation through examination of conscience and an act of contrition.",
    ],
  },
  {
    n: "vi",
    title: "Funeral Mass",
    subtitle: "Commending the Faithful to God\u2019s Mercy",
    schedule: "Every Saturday · 1:00 PM",
    description: [
      "Families may coordinate funeral arrangements with the Parish Office. The Funeral Mass commends the soul of the deceased to the loving mercy of God and offers consolation to the bereaved family in Christian hope.",
    ],
    requirements: [
      "Death Certificate (or proof of death from the funeral home).",
      "Baptismal & Confirmation Certificates of the deceased (if available).",
      "Coordinate with the Parish Office for time, music, and pastoral arrangements.",
    ],
  },
  {
    n: "vii",
    title: "House Blessing",
    subtitle: "Inviting Christ to Dwell in Our Homes",
    schedule: "By request",
    description: [
      "Families may request blessings for homes, religious articles, and special occasions through proper parish coordination.",
    ],
    requirements: [
      "Request through the Parish Office, indicating the date and address.",
    ],
  },
];

export default function Sacraments() {
  const [open, setOpen] = useState(0);

  return (
    <section id="sacraments" className="py-24 md:py-36 bg-parchment-200 relative grain">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(194,89,83,0.06),transparent_55%)]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel
          kicker="Pastoral Services"
          title="Sacraments of the Parish"
          intro="Each sacrament is a sacred encounter with Christ. Click any sacrament to read its meaning, schedule, and the documents required."
        />

        <div className="mt-16 bg-parchment-50 border border-parchment-400 divide-y divide-parchment-400">
          {sacraments.map((s, idx) => {
            const isOpen = open === idx;
            return (
              <article key={s.title}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : idx)}
                  className="w-full grid grid-cols-12 gap-4 items-baseline px-6 md:px-10 py-6 text-left hover:bg-parchment-100 transition-colors"
                >
                  <span className="col-span-2 md:col-span-1 font-serif italic text-2xl text-terracotta/80">
                    {s.n}.
                  </span>
                  <div className="col-span-10 md:col-span-8">
                    <h3 className="font-serif text-2xl md:text-3xl text-cocoa-900 leading-tight">
                      {s.title}
                    </h3>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mt-1.5 font-sans">
                      {s.subtitle}
                    </div>
                  </div>
                  <div className="col-span-12 md:col-span-3 md:text-right flex md:justify-end items-center gap-3">
                    <span className="font-sans text-[11px] uppercase tracking-[0.2em] text-terracotta">
                      {s.schedule}
                    </span>
                    <ChevronDown
                      size={16}
                      className={`text-cocoa-700 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                </button>

                {isOpen && (
                  <div className="px-6 md:px-10 pb-10 bg-parchment-100/40 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-7 space-y-4 font-sans text-cocoa-800 leading-relaxed font-light text-[15px]">
                      {s.description.map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                      {s.extra && (
                        <div className="mt-2 flex items-start gap-3 border-l-2 border-terracotta pl-4 italic text-cocoa-900">
                          <CalendarClock size={16} className="text-terracotta mt-1 flex-shrink-0" />
                          <span>{s.extra}</span>
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-5">
                      <div className="bg-cocoa-900 text-parchment-100 p-6 md:p-7">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-4 font-sans">
                          Requirements
                        </div>
                        <ul className="space-y-3 font-sans text-sm leading-relaxed">
                          {s.requirements.map((r, i) => (
                            <li key={i} className="flex gap-3 items-baseline">
                              <span className="text-gold font-serif text-sm flex-shrink-0">
                                {String(i + 1).padStart(2, "0")}
                              </span>
                              <span className="text-parchment-200/90 font-light">{r}</span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-6 pt-5 border-t border-parchment-100/15 flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-parchment-200/70">
                          <Phone size={14} className="text-gold" />
                          <a href="tel:+639155811419" className="hover:text-gold">
                            Parish Office · 0915 581 1419
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
